import React from 'react';
import { Stack, Box, Tooltip, Typography, Card, CardHeader, CardContent } from '@mui/material';
import PropTypes from 'prop-types';
import { styled, keyframes } from '@mui/system';

const loadBar = keyframes`
  from { width: 0; }
  to { width: var(--final-width); }
`;

const ProgressBar = styled(Box)(({ width, color, delay }) => ({
  width: '0%',
  height: '20px',
  backgroundColor: color,
  borderRadius: '4px',
  animation: `${loadBar} 0.6s ${delay}s forwards`,
  '--final-width': `${width}%`
}));

const colorMap = {
  current: '#83c6f4',
  '1_15_days': '#f26161',
  '16_30_days': '#f03939',
  '31_45_days': '#ea0909',
  '46_plus_days': '#9a0707'
};

const MultiRangeProgressBar = ({ data, currency }) => {
  const { overdue, overduePercentages, total } = data;

  const totalCount = total.count || 1;
  const overdueCountPercentages = Object.fromEntries(
    Object.entries(overdue).map(([key, val]) => [key, (val.count / totalCount) * 100])
  );

  const getLabel = (key) => {
    switch (key) {
      case 'current':
        return 'CURRENT';
      case '1_15_days':
        return '1-15 Days';
      case '16_30_days':
        return '16-30 Days';
      case '31_45_days':
        return '31-45 Days';
      case '46_plus_days':
        return 'Above 45 Days';
      default:
        return '';
    }
  };

  return (
    <Box sx={{ mt: 2, mb: 2 }}>
      <Card>
        <CardHeader
          title="Total Receivables"
          subheader={`${currency} ${total.amount} (Count: ${total.count})`}
        />
        <CardContent>
          {/* Amount Bar */}
          <Typography variant="subtitle2" gutterBottom>
            Amount Distribution
          </Typography>
          <Box
            sx={{
              display: 'flex',
              height: '20px',
              borderRadius: '5px',
              overflow: 'hidden',
              mb: 1
            }}
          >
            {Object.entries(overdue).map(([key, val], idx) => (
              <Tooltip
                key={key}
                arrow
                placement="top"
                title={
                  <Box sx={{ p: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {getLabel(key)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Amount:</strong> {currency} {val.amount}
                    </Typography>
                  </Box>
                }
              >
                <ProgressBar
                  width={overduePercentages[key]}
                  color={colorMap[key]}
                  delay={idx * 0.2}
                />
              </Tooltip>
            ))}
          </Box>

          <Typography variant="subtitle2" gutterBottom>
            Count Distribution
          </Typography>
          <Box
            sx={{
              display: 'flex',
              height: '20px',
              borderRadius: '5px',
              overflow: 'hidden',
              mb: 2
            }}
          >
            {Object.entries(overdue).map(([key, val], idx) => (
              <Tooltip
                key={key}
                arrow
                placement="top"
                title={
                  <Box sx={{ p: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {getLabel(key)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Count:</strong> {val.count}
                    </Typography>
                  </Box>
                }
              >
                <ProgressBar
                  width={overdueCountPercentages[key]}
                  color={colorMap[key]}
                  delay={idx * 0.2}
                  sx={{ opacity: 0.6 }}
                />
              </Tooltip>
            ))}
          </Box>

          {/* Labels */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
            {Object.entries(overdue).map(([key, val]) => (
              <Box key={key} sx={{ textAlign: 'center', minWidth: '60px', mb: 1 }}>
                <Typography variant="body2">{`${currency} ${val.amount}`}</Typography>
                <Typography variant="body2" color="textSecondary">
                  {`Count: ${val.count}`}
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="center"
                  sx={{ mt: 0.5 }}
                >
                  <Box
                    height={10}
                    width={10}
                    sx={{ bgcolor: colorMap[key], borderRadius: '2px' }}
                  />
                  <Typography color="textSecondary" variant="caption">
                    {getLabel(key)}
                  </Typography>
                </Stack>
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

MultiRangeProgressBar.propTypes = {
  currency: PropTypes.string.isRequired,
  data: PropTypes.shape({
    total: PropTypes.shape({
      amount: PropTypes.number.isRequired,
      count: PropTypes.number.isRequired
    }).isRequired,
    overdue: PropTypes.shape({
      current: PropTypes.shape({
        amount: PropTypes.number.isRequired,
        count: PropTypes.number.isRequired
      }).isRequired,
      '1_15_days': PropTypes.shape({
        amount: PropTypes.number.isRequired,
        count: PropTypes.number.isRequired
      }).isRequired,
      '16_30_days': PropTypes.shape({
        amount: PropTypes.number.isRequired,
        count: PropTypes.number.isRequired
      }).isRequired,
      '31_45_days': PropTypes.shape({
        amount: PropTypes.number.isRequired,
        count: PropTypes.number.isRequired
      }).isRequired,
      '46_plus_days': PropTypes.shape({
        amount: PropTypes.number.isRequired,
        count: PropTypes.number.isRequired
      }).isRequired
    }).isRequired,
    overduePercentages: PropTypes.shape({
      current: PropTypes.number.isRequired,
      '1_15_days': PropTypes.number.isRequired,
      '16_30_days': PropTypes.number.isRequired,
      '31_45_days': PropTypes.number.isRequired,
      '46_plus_days': PropTypes.number.isRequired
    }).isRequired
  }).isRequired
};

export default MultiRangeProgressBar;
